/** Driving distance helpers shared by Calculator and Quick Delivery Estimate. */

export const ORIGIN_ADDRESS = '258 Mason Road, La Vergne, TN 37086';

/**
 * Load the Google Maps JS API (places + Distance Matrix) once.
 * Resolves when maps.places is available; no-ops if already loaded.
 */
export function loadGoogleMapsApi() {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  if (!key) {
    return Promise.reject(new Error('Google Maps API key not configured.'));
  }

  if (window.google?.maps?.places && window.google?.maps?.DistanceMatrixService) {
    return Promise.resolve(window.google);
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      const onReady = () => {
        if (window.google?.maps?.places) resolve(window.google);
        else reject(new Error('Google Maps API failed to initialize.'));
      };
      if (window.google?.maps?.places) {
        onReady();
        return;
      }
      existing.addEventListener('load', onReady, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps API.')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.maps?.places) resolve(window.google);
      else reject(new Error('Google Maps API failed to initialize.'));
    };
    script.onerror = () => reject(new Error('Failed to load Google Maps API.'));
    document.head.appendChild(script);
  });
}

/**
 * Driving distance in miles from Mayker HQ to destination (city, address, or ZIP).
 */
export function calculateDrivingDistance(destinationAddress) {
  return new Promise((resolve, reject) => {
    if (!destinationAddress || !String(destinationAddress).trim()) {
      reject(new Error('Please enter a destination.'));
      return;
    }
    if (!window.google?.maps?.DistanceMatrixService) {
      reject(new Error('Google Maps is not available. Enter a market manually or try again.'));
      return;
    }

    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [ORIGIN_ADDRESS],
        destinations: [destinationAddress],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      },
      (response, status) => {
        if (status === 'OK') {
          const element = response?.rows?.[0]?.elements?.[0];
          if (element?.status === 'OK') {
            resolve(element.distance.value / 1609.34);
            return;
          }
          reject(
            new Error(
              `Unable to calculate distance${element?.status ? `: ${element.status}` : ''}. Check the destination and try again.`
            )
          );
          return;
        }
        if (status === 'ZERO_RESULTS') {
          reject(new Error('No route found. Please check the destination.'));
          return;
        }
        if (status === 'REQUEST_DENIED') {
          reject(new Error('Distance lookup unavailable. Please select a market manually.'));
          return;
        }
        reject(new Error(`Unable to calculate distance (${status}). Please try again.`));
      }
    );
  });
}

/**
 * Attach Places Autocomplete to an input. Prefers geocode so ZIP / city / address work.
 * Returns a cleanup function.
 */
export function attachPlacesAutocomplete(inputEl, { onPlace, country = 'us' } = {}) {
  if (!inputEl || !window.google?.maps?.places) return () => {};
  if (inputEl.dataset.autocompleteInitialized === 'true') return () => {};

  const autocomplete = new window.google.maps.places.Autocomplete(inputEl, {
    types: ['geocode'],
    componentRestrictions: { country },
  });
  inputEl.dataset.autocompleteInitialized = 'true';

  const listener = autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace();
    const value = place.formatted_address || place.name || '';
    if (value && typeof onPlace === 'function') onPlace(value, place);
  });

  return () => {
    if (listener && window.google?.maps?.event) {
      window.google.maps.event.removeListener(listener);
    }
    delete inputEl.dataset.autocompleteInitialized;
  };
}
